import {
  cloneDeep,
  isArray,
  isBoolean,
  isFinite,
  isFunction,
  keys,
  last,
  values,
  first,
} from 'lodash'
import {toJS} from 'mobx'
import {sprintf} from 'sprintf-js'

///////////////////////////////////////////////////////////////////////////////

export type CompilerArgs = {
  debug?: boolean
  methods?: CompilerMethods
  cache?: CompilerCache
}

type Sequence = {
  type: string
  value: string
  children: Sequence[]
  parent: Sequence
}

export type FunctionMocks = {
  [key: string]: (() => any) | any
}

export type CompilerMethod = (...args) => any

export type CompilerMethods = {
  [key: string]: CompilerMethod
}

export type CompilerCache = {
  [key: string]: Sequence[]
}

export type ConfigCondition<T = any> = T | Condition<T>

export type Condition<T = any> = {
  if: string | boolean
  then?: ConfigCondition<T> | ConfigCondition<T>[]
  else?: ConfigCondition<T> | ConfigCondition<T>[]
}

export type ComputedField = {
  field: string
  format: string
}

///////////////////////////////////////////////////////////////////////////////

enum Token {
  BRACE_OPEN = 'BRACE_OPEN',
  BRACE_CLOSE = 'BRACE_CLOSE',
  BRACKET_OPEN = 'BRACKET_OPEN',
  BRACKET_CLOSE = 'BRACKET_CLOSE',
  GAP = 'GAP',
  COMMA = 'COMMA',
  END = 'END',
  FUNCTION = 'FUNCTION',
  SKIP = 'SKIP',
  AND = 'AND',
  AND2 = 'AND',
  OR = 'OR',
  OR2 = 'OR',
  VALUE = 'VALUE',
  ARR = 'ARR',
  OPERATOR = 'OPERATOR',
  LOGICAL = 'LOGICAL',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
  EQ = 'EQ',
  EQS = 'EQS',
  NEQ = 'NEQ',
  NEQS = 'NEQS',
  ADD = 'ADD',
  SUB = 'SUB',
  MUL = 'MUL',
  DIV = 'DIV',
  MOD = 'MOD',
  NAN = 'NAN',
  IN = 'IN',
  NIN = 'NIN',
  NEG = 'NEG',
  NEG2 = 'NEG',
  NEG3 = 'NEG',
  VAL = 'VAL',
}

const Literal = {
  '(': Token.BRACE_OPEN,
  ')': Token.BRACE_CLOSE,
  '[': Token.BRACKET_OPEN,
  ']': Token.BRACKET_CLOSE,
  ',': Token.COMMA,
  ';': Token.END,
  and: Token.AND,
  '&&': Token.AND2,
  or: Token.OR,
  '||': Token.OR2,
  '>': Token.GT,
  '>=': Token.GTE,
  '<': Token.LT,
  '<=': Token.LTE,
  '==': Token.EQ,
  '===': Token.EQS,
  '!=': Token.NEQ,
  '!==': Token.NEQS,
  '+': Token.ADD,
  '-': Token.SUB,
  '*': Token.MUL,
  '/': Token.DIV,
  '%': Token.MOD,
  '!': Token.NEG,
  not: Token.NEG2,
  unless: Token.NEG3,
  in: Token.IN,
  nin: Token.NIN,
}

const MathematicalOperators = [
  Token.GT,
  Token.GTE,
  Token.LT,
  Token.LTE,
  Token.EQ,
  Token.EQS,
  Token.NEQ,
  Token.NEQS,
  Token.ADD,
  Token.SUB,
  Token.MUL,
  Token.DIV,
  Token.MOD,
  Token.NAN,
  Token.IN,
  Token.NIN,
]

const LogicalOperators = [
  Token.AND,
  Token.AND2,
  Token.OR,
  Token.OR2,
  Token.NEG,
  Token.NEG2,
  Token.NEG3,
]

const LiteralMethods = [Token.AND, Token.OR, Token.IN, Token.NIN]

const BraceLessMethods = [Token.NEG, Token.NEG2, Token.NEG3]

const StdCompilerMethods = {
  and: (x, y) => x && y,
  or: (x, y) => x || y,
  gt: (x, y) => x > y,
  gte: (x, y) => x >= y,
  lt: (x, y) => x < y,
  lte: (x, y) => x <= y,
  eq: (x, y) => x == y,
  eqs: (x, y) => x === y,
  neq: (x, y) => x != y,
  neqs: (x, y) => x !== y,
  add: (x, y) => x + y,
  sub: (x, y) => x - y,
  mul: (x, y) => x * y,
  div: (x, y) => x / y,
  mod: (x, y) => x % y,
  arr: (...values) => values,
  round: (x) => Math.round(x),
  ceil: (x) => Math.ceil(x),
  floor: (x) => Math.floor(x),
  nan: (x) => x.toString().indexOf(' ') !== -1 || isNaN(x),
  in: (x, y) => y.indexOf(x) !== -1,
  nin: (x, y) => y.indexOf(x) === -1,
  neg: (x) => !x,
  val: (x) => x,
  toUpperCase: (x) => x.toString().toUpperCase(),
  toLowerCase: (x) => x.toString().toLowerCase(),
  fmt: (f, ...v) => {
    if (f.toString().indexOf('%s') === -1) {
      v.unshift(f)
      return v.join('')
    }
    return sprintf(f.toString(), ...v)
  },
  join: (arr, joint) => arr.join(joint),
}

///////////////////////////////////////////////////////////////////////////////

export class Compiler {
  public debug: boolean
  private methods: CompilerMethods
  private cache: CompilerCache

  constructor(args: CompilerArgs = {}) {
    let {debug, methods, cache} = args

    debug = !!debug
    methods = methods || {}
    cache = cache || {}

    this.debug = debug
    this.methods = {...StdCompilerMethods, ...methods}
    this.cache = cache
  }

  addMethod(name: string, method: CompilerMethod) {
    this.addMethods({[name]: method})
  }

  addMethods(methods: CompilerMethods) {
    this.methods = {...this.methods, ...methods}
  }

  getSupportedMethods(): string[] {
    return keys(this.methods)
  }

  getSupportedOperators(): string[] {
    let operators = [
      ...values(LogicalOperators),
      ...values(MathematicalOperators),
    ]
    return keys(Literal).filter((key) => {
      let token = Literal[key]

      return operators.indexOf(token) !== -1
    })
  }

  compile(statement: string, mocks: FunctionMocks = null): any {
    let sequences = this.parse(statement)
    let result = this.execute(sequences, mocks)
    this.debug && console.log('result:', result)
    this.debug && console.log('statement:', statement, 'result:', result)

    return result
  }

  checkCondition<T extends ConfigCondition>(
    condition: T,
    mocks: FunctionMocks = null,
  ): boolean {
    if (condition !== undefined && (condition as Condition).if !== undefined) {
      if (isBoolean((condition as Condition).if)) {
        return !!(condition as Condition).if
      }

      return !!this.compile((condition as Condition).if as string, mocks)
    }

    return !!condition
  }

  interpolate(
    lines: string | string[],
    mocks: FunctionMocks = null,
  ): string | string[] {
    let arrayOfLines = isArray(lines)
    lines = arrayOfLines ? lines : ([lines] as string[])

    let interpolatedLines = []

    for (let line of lines) {
      if (!line) {
        continue
      }

      // https://stackoverflow.com/a/406408
      let matches = line.match(/%%((?!%%).)*%%/g) || []

      for (let match of matches) {
        let expression = match.replace(/%%/g, '')
        let result = this.compile(expression, mocks)

        line = line.replace('%%' + expression + '%%', result)
      }

      interpolatedLines.push(line)
    }

    return arrayOfLines ? interpolatedLines : first(interpolatedLines)
  }

  resolveConditionWithSingleResult<T>(
    condition: ConfigCondition<T> | ConfigCondition<T>[],
    mocks: FunctionMocks = null,
  ): any {
    let result = this.resolveCondition(condition, mocks)

    if (isArray(result)) {
      return first(result)
    }

    return result
  }

  resolveCondition<T>(
    condition: ConfigCondition<T> | ConfigCondition<T>[],
    mocks: FunctionMocks = null,
  ): any {
    // condition is undefined or null
    if (condition === undefined || condition === null) {
      return null
    }

    // condition is actually a condition
    else if ((condition as Condition<T>).if !== undefined) {
      // condition was truthy
      if (this.checkCondition(condition, mocks)) {
        // condition has a custom result
        if ((condition as Condition<T>).then !== undefined) {
          // if there is a boolean result on the "then" branch, return it
          if (isBoolean((condition as Condition<T>).then)) {
            return (condition as Condition<T>).then
          } else {
            // process custom result
            return this.resolveCondition(
              (condition as Condition<T>).then,
              mocks,
            )
          }
        }
        // condition has no custom result
        else {
          return null
        }
      }
      // condition was falsy
      else {
        // condition has a custom result
        if ((condition as Condition<T>).else !== undefined) {
          // if there is a boolean result on the "else" branch, return it
          if (isBoolean((condition as Condition<T>).else)) {
            return (condition as Condition<T>).else
          } else {
            // process custom result
            return this.resolveCondition(
              (condition as Condition<T>).else,
              mocks,
            )
          }
        }
        // condition has no custom result
        else {
          return null
        }
      }
    }

    // condition might be an array of conditions
    else if (isArray(toJS(condition))) {
      let results: T[] = []

      ;(condition as Condition<T>[]).forEach((condition) => {
        let result = this.resolveCondition(condition, mocks)

        if (isArray(toJS(result))) {
          results.push(...result)
        } else if (result !== null) {
          results.push(result)
        }
      })

      return results
    }

    // not a condition - it's just a value
    else {
      return condition
    }
  }

  private createLiterals(statement: string): string[] {
    statement += ';'
    let chars = statement.split('')
    let buffer = ''
    let literals: string[] = []
    let skip = 0
    let quotesOpen = false

    chars.forEach((current, index) => {
      let previous = chars[index - 1]
      let next = chars[index + 1]
      let escaped = previous === '\\'

      if (current === Token.SKIP) {
        return
      }

      if (escaped) {
        buffer += current
      } else if (current === '\\') {
      } else if (current === '"') {
        quotesOpen = !quotesOpen
      } else if (current === ' ') {
        if (quotesOpen) {
          buffer += current
        } else if (buffer.length > 0) {
          literals.push(buffer)
          buffer = ''

          if (next === '(') {
            literals.push(Token.GAP)
          }
        }
      } else if (keys(Literal).indexOf(current) === -1) {
        buffer += current
      } else if (quotesOpen) {
        buffer += current
      } else {
        if (buffer.length > 0) {
          literals.push(buffer)
          buffer = ''
        }

        let nextPosition = index + 1
        while (true) {
          let next = chars[nextPosition]

          if (next === undefined) {
            break
          }

          if (Literal[current + next]) {
            current = current + next
            skip++
            nextPosition++
          } else {
            break
          }
        }

        let token = Token[Literal[current]]

        if (!token) {
          throw new Error('No token found for literal "' + current + '"')
        }

        literals.push(token)
      }

      while (skip > 0) {
        chars[index + skip] = Token.SKIP
        skip--
      }
    })

    return literals
  }

  private createMultiCharLiterals(literals: string[]): string[] {
    let multiCharLiterals: string[] = []

    literals.forEach((current) => {
      if (keys(Literal).indexOf(current) === -1) {
        multiCharLiterals.push(current)
      } else {
        let multiCharLiteral = Token[Literal[current]]

        if (!multiCharLiteral) {
          throw new Error('No literal found for word "' + current + '"')
        }

        multiCharLiterals.push(multiCharLiteral)
      }
    })

    return multiCharLiterals
  }

  private normalizeValues(value: any): any {
    if (isArray(value)) {
      return value.map((v) => this.normalizeValues(v))
    }

    if (value === 'true') {
      return true
    } else if (value === 'false') {
      return false
    } else if (
      value === '' ||
      value === 'null' ||
      value === 'undefined' ||
      value === null ||
      value === undefined
    ) {
      return null
    } else if (value.toString().indexOf(' ') !== -1) {
      return value
    } else if (isFinite(Number(value))) {
      return parseFloat(value)
    }

    return value
  }

  private createSequences(literals: string[]): Sequence[] {
    let previous = null
    let next = null
    let sequences: Sequence[] = []
    let currentSequence = null

    literals.forEach((current, index) => {
      let skip = 0

      if (literals[index + 1] !== undefined) {
        next = literals[index + 1]
      } else {
        next = null
      }

      if (current === Token.GAP) {
      } else if (current === Token.BRACE_OPEN) {
        if (!currentSequence) {
          currentSequence = this.createSequence(
            Token.FUNCTION,
            Token.VAL.toLowerCase(),
          )
          sequences.push(currentSequence)
        } else {
          currentSequence = this.createSequence(
            Token.FUNCTION,
            Token.VAL.toLowerCase(),
            null,
            currentSequence,
          )
        }
      } else if (BraceLessMethods.indexOf(current as Token) !== -1) {
        if (!currentSequence) {
          currentSequence = this.createSequence(
            Token.FUNCTION,
            current.toLowerCase(),
          )
          sequences.push(currentSequence)
        } else {
          currentSequence = this.createSequence(
            Token.FUNCTION,
            current.toLowerCase(),
            null,
            currentSequence,
          )
        }

        if (next === Token.BRACE_OPEN) {
          skip++
        }
      } else if (
        values(Token).indexOf(current as Token) === -1 ||
        values(LiteralMethods).indexOf(current as Token) !== -1
      ) {
        if (next === Token.BRACE_OPEN) {
          skip++

          let method =
            values(LiteralMethods).indexOf(current as Token) !== -1
              ? current.toLowerCase()
              : current

          if (!currentSequence) {
            currentSequence = this.createSequence(Token.FUNCTION, method)
            sequences.push(currentSequence)
          } else {
            currentSequence = this.createSequence(
              Token.FUNCTION,
              method,
              null,
              currentSequence,
            )
          }
        } else if (currentSequence) {
          let type =
            (MathematicalOperators.indexOf(current as Token) !== -1 &&
              Token.OPERATOR) ||
            (LogicalOperators.indexOf(current as Token) !== -1 &&
              Token.LOGICAL) ||
            Token.VALUE
          currentSequence.children.push(this.createSequence(type, current))
        } else if (values(LiteralMethods).indexOf(current as Token) !== -1) {
          if (values(LogicalOperators).indexOf(current as Token) !== -1) {
            sequences.push(this.createSequence(Token.LOGICAL, current))
          } else {
            sequences.push(this.createSequence(Token.OPERATOR, current))
          }
        } else {
          sequences.push(this.createSequence(Token.VALUE, current))
        }
      } else if (current === Token.BRACE_CLOSE) {
        if (currentSequence && currentSequence.parent) {
          currentSequence = currentSequence.parent
        } else {
          currentSequence = null
        }
      } else if (current === Token.BRACKET_OPEN) {
        if (!currentSequence) {
          currentSequence = this.createSequence(
            Token.FUNCTION,
            Token.ARR.toLowerCase(),
          )
          sequences.push(currentSequence)
        } else {
          currentSequence = this.createSequence(
            Token.FUNCTION,
            Token.ARR.toLowerCase(),
            null,
            currentSequence,
          )
        }
      } else if (current === Token.BRACKET_CLOSE) {
        if (currentSequence && currentSequence.parent) {
          currentSequence = currentSequence.parent
        } else {
          currentSequence = null
        }
      } else if (MathematicalOperators.indexOf(current as Token) !== -1) {
        if (currentSequence) {
          currentSequence.children.push(
            this.createSequence(Token.OPERATOR, current),
          )
        } else {
          sequences.push(this.createSequence(Token.OPERATOR, current))
        }
      } else if (LogicalOperators.indexOf(current as Token) !== -1) {
        if (currentSequence) {
          currentSequence.children.push(
            this.createSequence(Token.LOGICAL, current),
          )
        } else {
          sequences.push(this.createSequence(Token.LOGICAL, current))
        }
      } else if (
        [Token.COMMA, Token.SKIP, Token.END].indexOf(current as Token) !== -1
      ) {
      } else {
        throw new Error('Unexpected token "' + current + '"')
      }

      while (skip > 0) {
        literals[index + skip] = Token.SKIP
        skip--
      }

      previous = current
    })

    return sequences
  }

  private createSequence(
    type: string,
    value = null,
    children: Sequence[] = null,
    parent: Sequence = null,
  ): Sequence {
    children = children || []
    parent = parent || null

    let sequence: Sequence = {type, value, children, parent}

    if (parent) {
      parent.children.push(sequence)
    }

    return sequence
  }

  private reorderSequences(sequences: Sequence[]): Sequence[] {
    sequences = cloneDeep(sequences)
    let reorderedSequences: Sequence[] = []
    let skip = 0

    sequences.forEach((current, index) => {
      let previousReorderedSequence = last(reorderedSequences)
      let nextSequence = sequences[index + 1]

      if (current.type === Token.OPERATOR) {
        if (!previousReorderedSequence) {
          throw new Error(
            'Missing left hand assignment before "' + current.value + '"',
          )
        }

        if (!nextSequence) {
          throw new Error(
            'Missing right hand assignment after "' + current.value + '"',
          )
        }

        current.children.push(reorderedSequences.pop())
        current.children.push(nextSequence)
        current.children = this.reorderSequences(current.children)
        current.type = Token.FUNCTION
        current.value = current.value.toLowerCase()
        reorderedSequences.push(current)
        skip++
      } else if (current.type !== Token.SKIP) {
        if (current.type === Token.FUNCTION) {
          current.children = this.reorderSequences(current.children)
        }

        reorderedSequences.push(current)
      }

      while (skip > 0) {
        sequences[index + skip] = this.createSequence(Token.SKIP)
        skip--
      }
    })

    return reorderedSequences
  }

  private reorderLogicalSequences(sequences: Sequence[]): Sequence[] {
    sequences = cloneDeep(sequences)
    let logicalSequences: Sequence[] = []
    let skip = 0

    sequences.forEach((current, index) => {
      let previousLogicalSequence = last(logicalSequences)
      let nextSequence = sequences[index + 1]

      if (current.type === Token.LOGICAL) {
        if (!previousLogicalSequence) {
          throw new Error(
            'Missing left hand assignment before "' + current.value + '"',
          )
        }

        if (!nextSequence) {
          throw new Error(
            'Missing right hand assignment after "' + current.value + '"',
          )
        }

        current.children.push(logicalSequences.pop())
        current.children.push(nextSequence)
        current.children = this.reorderLogicalSequences(current.children)
        current.type = Token.FUNCTION
        current.value = current.value.toLowerCase()
        logicalSequences.push(current)
        skip++
      } else if (current.type !== Token.SKIP) {
        if (current.type === Token.FUNCTION) {
          current.children = this.reorderLogicalSequences(current.children)
        }

        logicalSequences.push(current)
      }

      while (skip > 0) {
        sequences[index + skip] = this.createSequence(Token.SKIP)
        skip--
      }
    })

    return logicalSequences
  }

  private parse(statement: string): Sequence[] {
    this.debug && console.log('statement:', statement)

    if (this.cache && this.cache[statement]) {
      this.debug && console.log('found in cache:', this.cache[statement])
      return this.cache[statement]
    }

    let literals = this.createLiterals(statement)
    this.debug && console.log('literals:', literals)

    let multiCharLiterals = this.createMultiCharLiterals(literals)
    this.debug && console.log('multi char literals:', multiCharLiterals)

    let normalizedLiterals = this.normalizeValues(multiCharLiterals)
    this.debug && console.log('normalized literals:', normalizedLiterals)

    let sequences = this.createSequences(normalizedLiterals)
    this.debug && console.log('sequences:', sequences)

    let reorderedSequences = this.reorderSequences(sequences)
    this.debug && console.log('reordered sequences:', reorderedSequences)

    let reorderedLogicalSequences = this.reorderLogicalSequences(
      reorderedSequences,
    )
    this.debug &&
      console.log('reordered logical sequences:', reorderedLogicalSequences)

    if (this.cache) {
      this.cache[statement] = reorderedLogicalSequences
    }

    return reorderedLogicalSequences
  }

  private execute(sequences: Sequence[], mocks: FunctionMocks = null): any {
    return this.executeNested(sequences, mocks).shift()
  }

  private executeNested(
    sequences: Sequence[],
    mocks: FunctionMocks = null,
  ): any[] {
    mocks = mocks || {}

    let results = sequences.map((current) => {
      if (current.type === Token.FUNCTION) {
        let mockMethod = mocks[current.value]
        let method = this.methods[current.value]

        if (mockMethod) {
          if (!isFunction(mockMethod)) {
            method = () => mockMethod
          } else {
            method = mockMethod
          }
        }

        if (!method) {
          throw new Error('Unknown method "' + current.value + '"')
        }

        let args = this.executeNested(current.children, mocks)
        return method(...args)
      } else if (current.type === Token.VALUE) {
        return current.value
      }
    })

    return results
  }
}

///////////////////////////////////////////////////////////////////////////////

export function createCompiler(args?: CompilerArgs) {
  return new Compiler(args)
}
